import { DialogComponent } from '@theme/dialog';

/**
 * Product share dialog.
 * Copies the product URL and leaves network links to the browser.
 *
 * @typedef {object} ProductShareRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [status]
 *
 * @extends {DialogComponent}
 */
class ProductShareComponent extends DialogComponent {
  /** @type {number | undefined} */
  #copyTimer;

  disconnectedCallback() {
    window.clearTimeout(this.#copyTimer);
    super.disconnectedCallback();
  }

  /**
   * Copies the product URL and announces the result.
   */
  async copyLink() {
    const url = this.dataset.shareUrl;
    const status = this.refs.status;

    if (!url || !(status instanceof HTMLElement)) return;

    const copied = await this.#writeClipboard(url);
    status.textContent = copied ? (this.dataset.copiedMessage ?? '') : (this.dataset.copyFailedMessage ?? '');

    window.clearTimeout(this.#copyTimer);
    this.#copyTimer = window.setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }

  /**
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  async #writeClipboard(url) {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      const field = document.createElement('textarea');
      const dialog = this.refs.dialog;
      field.value = url;
      field.setAttribute('readonly', '');
      field.setAttribute('aria-hidden', 'true');
      field.tabIndex = -1;
      field.style.position = 'fixed';
      field.style.opacity = '0';
      const parent = dialog instanceof HTMLElement ? dialog : this;
      parent.append(field);
      field.focus();
      field.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }

      field.remove();
      return copied;
    }
  }
}

if (!customElements.get('product-share-component')) {
  customElements.define('product-share-component', ProductShareComponent);
}
